// Calendar dates where the operator is — never the UTC date.
//
// `new Date().toISOString().split("T")[0]` is the UTC date: at 23:30 in
// Canada it is already tomorrow, and before noon in New Zealand it is still
// yesterday. `new Date("YYYY-MM-DD")` has the same problem the other way
// round: it is UTC midnight, i.e. the previous evening in the Americas.

const localDateKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** The browser's own IANA zone, or `null` if it will not say. */
const browserTimeZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/** Today's calendar date (YYYY-MM-DD) in `timeZone` (an IANA name such as
 *  "America/Regina"), or in the browser's own zone when none is given or the
 *  name is not one this browser knows. Never the UTC date (U30). */
export function calendarDateIn(now: Date, timeZone?: string | null): string {
  for (const tz of [(timeZone ?? "").trim(), browserTimeZone()]) {
    if (!tz) continue;
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
      }).formatToParts(now);
      const get = (t: string) => parts.find((p) => p.type === t)?.value;
      const y = get("year");
      const m = get("month");
      const d = get("day");
      if (y && m && d) return `${y}-${m}-${d}`;
    } catch {
      // An unknown zone name: try the next one.
    }
  }
  return localDateKey(now); // the Date's own local fields
}

/** A bare calendar date, "YYYY-MM-DD", with no time part. */
export const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** The tenant's IANA zone from a `global` config read — `global_timezone`,
 *  which the config API serves as `timezone` (prefix stripped). The zone the
 *  backend's `tenant_now` stamps operator time in. `null` if unset. */
export function tenantTimeZone(
  globalConfig: Record<string, any> | null | undefined
): string | null {
  const raw = globalConfig?.global_timezone ?? globalConfig?.timezone;
  const tz = typeof raw === "string" ? raw.trim() : "";
  return tz || null;
}

/** Today's date, YYYY-MM-DD, where the OPERATOR is — the default for every
 *  new flight (H125, Aaron 2026-09-28: "default new flight date to today").
 *  Falls back to the browser's zone, never the UTC date. */
export function tenantToday(
  globalConfig: Record<string, any> | null | undefined,
  now: Date = new Date()
): string {
  return calendarDateIn(now, tenantTimeZone(globalConfig));
}
